import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type CountSheetPayload, type ValidationReport } from '../api/client';
import { generateCountSheet, parseCountSheet } from '../lib/countsheet';
import { ProjectSteps } from '../components/ProjectSteps';
import { Hint, HINTS } from '../components/Hint';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

// Count Sheet (§5.6 / §5.7). One click generates the branded Symbol Quantity
// Excel; the human fills QTY and drops it back. Re-import matches by hidden Row ID
// and returns a validation report. The software NEVER counts.
export function CountSheet() {
  const { t } = useLang();
  const { id = '' } = useParams();
  const [data, setData] = useState<CountSheetPayload | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [report, setReport] = useState<ValidationReport | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function load() {
    try { setData(await api.getCountSheet(id)); }
    catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function generate() {
    if (!data) return;
    setBusy('Generating branded Excel…'); setError('');
    try {
      const today = new Date().toISOString().slice(0, 10);
      const blob = await generateCountSheet(data.project, data.rows, today);
      const safe = data.project.name.replace(/[^\w.-]+/g, '_').slice(0, 60) || 'project';
      downloadBlob(blob, `${safe}_Symbol_Quantity_Count_Sheet.xlsx`);
    } catch (e) { setError(e instanceof Error ? e.message : 'Generate failed'); }
    finally { setBusy(''); }
  }

  async function importFile(file: File) {
    setBusy(`Reading ${file.name}…`); setError(''); setReport(null);
    try {
      const rows = await parseCountSheet(file);
      setBusy('Validating and importing…');
      const res = await api.importCounts(id, rows);
      setReport(res.report);
      await load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Import failed'); }
    finally { setBusy(''); if (fileRef.current) fileRef.current.value = ''; }
  }

  // ONE-BUTTON AUTO-PILOT: counts → BOM → pricing → estimate, in sequence.
  // The human's job ended at counting; everything here is automatic.
  const [bidBusy, setBidBusy] = useState('');
  const [bidError, setBidError] = useState('');
  const [bid, setBid] = useState<{ sell: number; lines: number; no_assembly: string[]; uncounted: string[] } | null>(null);

  async function generateBid() {
    setBidBusy('Cascading your counts into the BOM…'); setBidError(''); setBid(null);
    try {
      const bom = await api.generateBom(id);
      setBidBusy('Pricing from your rate library…');
      await api.repriceBom(id).catch(() => null);   // pricing is best-effort; estimate still computes
      setBidBusy('Computing the estimate…');
      const est = await api.saveEstimate(id);
      setBid({
        sell: est.final_sell_price,
        lines: bom.result.lines,
        no_assembly: bom.result.no_assembly,
        uncounted: bom.result.uncounted,
      });
      await load();
    } catch (e) {
      setBidError(e instanceof Error ? e.message : 'Bid generation failed');
    } finally { setBidBusy(''); }
  }

  const rows = data?.rows ?? [];
  const counted = rows.filter((r) => r.count != null).length;

  return (
    <div onKeyDown={(e) => { if (e.key === 'Enter' && fileRef.current) fileRef.current.click(); }}>
      <Link to={`/projects/${id}`} className="muted" style={{ fontSize: 13 }}>← {t({ en: 'Scope Analysis', fil: 'Scope Analysis' })}</Link>
      <h1 style={{ margin: '4px 0 0' }}>{t({ en: 'Count Sheet', fil: 'Count Sheet' })} — {data?.project?.name ?? t({ en: 'Project', fil: 'Proyekto' })}</h1>
      <div style={{ marginTop: 12 }}><ProjectSteps current="count" /></div>
      <p className="muted" style={{ marginTop: 4 }}>
        {t({ en: 'Generate the branded Symbol Quantity Excel, enter quantities by hand, then drop it back.', fil: 'I-generate ang branded na Symbol Quantity Excel, i-type nang manu-mano ang mga quantity, tapos i-drop ulit dito.' })}
        <strong> {t({ en: 'The software never counts — every quantity is yours.', fil: 'Hindi kailanman nagbibilang ang software — sa iyo ang lahat ng quantity.' })}</strong>
      </p>

      {error && <div className="error">{error}</div>}

      {rows.length > 0 && (
        <div className="card" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <strong>{t({ en: 'Want pictures of each symbol in the count sheet?', fil: 'Gusto mo ba ng larawan ng bawat symbol sa count sheet?' })}</strong>
            <div className="muted" style={{ fontSize: 13 }}>
              {t({ en: 'Run', fil: 'Patakbuhin ang' })} <strong>Legend Layout</strong> {t({ en: "to crop the actual glyphs from your plans' legend page and embed them in the Symbol column.", fil: 'para i-crop ang aktwal na mga glyph mula sa legend page ng plans mo at i-embed sa Symbol column.' })} {rows.filter((r) => r.symbol_image_ref).length} {t({ en: 'of', fil: 'sa' })} {rows.length} {t({ en: 'symbols currently have an image.', fil: 'symbols ang may larawan sa ngayon.' })}
            </div>
          </div>
          <Link to={`/projects/${id}/legend-layout`} className="btn btn-ghost">{t({ en: 'Open Legend Layout →', fil: 'Buksan ang Legend Layout →' })}</Link>
        </div>
      )}

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', marginTop: 8 }}>
        {/* Generate */}
        <div className="card">
          <h2>1 · {t({ en: 'Generate Symbol Quantity Excel', fil: 'I-generate ang Symbol Quantity Excel' })}</h2>
          <p className="muted" style={{ marginTop: 0 }}>
            {rows.length} symbol{rows.length === 1 ? '' : 's'} {t({ en: 'in scope (selected systems, excluding mapping-excluded).', fil: 'na nasa scope (mga piniling systems, hindi kasama ang mapping-excluded).' })}
            {counted > 0 && <> · {counted} {t({ en: 'already have counts (they round-trip).', fil: 'ang may count na (mababalik sila).' })}</>}
          </p>
          <button className="btn btn-gold" disabled={!rows.length || !!busy} onClick={generate}>
            {busy.startsWith('Generating') ? busy : t({ en: 'Generate Symbol Quantity Excel', fil: 'I-generate ang Symbol Quantity Excel' })}
          </button>
          {!rows.length && <p className="muted" style={{ marginBottom: 0 }}>{t({ en: 'No symbols yet — analyze the plan documents first.', fil: 'Wala pang symbols — i-analyze muna ang plan documents.' })}</p>}
        </div>

        {/* Import */}
        <div className="card">
          <h2>2 · {t({ en: 'Import completed sheet', fil: 'I-import ang kumpletong sheet' })}</h2>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) void importFile(f); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--teal)' : 'var(--gray-300)'}`, borderRadius: 8,
              padding: '22px 16px', textAlign: 'center', cursor: 'pointer',
              background: dragOver ? 'var(--teal-light)' : 'var(--gray-050)',
            }}
          >
            {busy && !busy.startsWith('Generating') ? <span>{busy}</span>
              : <span className="muted">{t({ en: 'Drop the completed', fil: 'I-drop ang kumpletong' })} <strong>.xlsx</strong> {t({ en: 'here, or click to choose. Press', fil: 'dito, o i-click para pumili. Pindutin ang' })} <kbd>Enter</kbd> {t({ en: 'to browse.', fil: 'para mag-browse.' })}</span>}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); }} />
        </div>
      </div>

      {/* Validation report (§9) */}
      {report && <ReportCard report={report} />}

      {report?.accepted ? (
        <div className="card" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
            <div>
              <h2 style={{ margin: 0 }}>3 · {t({ en: 'Generate the bid', fil: 'I-generate ang bid' })}</h2>
              <p className="muted" style={{ margin: '4px 0 0', maxWidth: 620 }}>
                {t({ en: "One click does the rest automatically: cascades your counts into the BOM, prices it from your rate library, and computes the estimate. Anything it couldn't price is listed below — never hidden.", fil: 'Isang click lang at automatic na ang lahat: ililipat ang counts mo sa BOM, ipe-price ito mula sa rate library mo, at kukwentahin ang estimate. Ang hindi ma-price ay nakalista sa baba — hindi itinatago.' })}
              </p>
            </div>
            <button className="btn btn-primary" disabled={!!bidBusy} onClick={generateBid} style={{ whiteSpace: 'nowrap' }}>
              {bidBusy || t({ en: 'Generate Bid →', fil: 'I-generate ang Bid →' })}
            </button>
          </div>
          {bidError && <div className="error" style={{ marginTop: 10 }}>{bidError}</div>}
          {bid && (
            <div className="banner" style={{ marginTop: 12 }}>
              <strong>{t({ en: 'Bid ready — sell price', fil: 'Handa na ang bid — sell price' })} ${bid.sell.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.</strong>{' '}
              {bid.lines} {t({ en: 'BOM line item', fil: 'BOM line item' })}{bid.lines === 1 ? '' : 's'}.
              {bid.no_assembly.length > 0 && (
                <div style={{ color: 'var(--warning)', marginTop: 4 }}>
                  <Hint text={HINTS.no_assembly}>{t({ en: 'Needs a part/price (no assembly yet — review)', fil: 'Kailangan ng part/price (wala pang assembly — i-review)' })}</Hint>: {bid.no_assembly.join(', ')}
                </div>
              )}
              {bid.uncounted.length > 0 && (
                <div style={{ color: 'var(--warning)', marginTop: 4 }}>
                  <Hint text={HINTS.uncounted}>{t({ en: 'Skipped (no count entered)', fil: 'Nilaktawan (walang count na nilagay)' })}</Hint>: {bid.uncounted.join(', ')}
                </div>
              )}
              <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <a className="btn btn-primary btn-sm" href={api.pdfUrl(id, 'proposal-short-pdf')} target="_blank" rel="noreferrer">{t({ en: 'Download Proposal (PDF)', fil: 'I-download ang Proposal (PDF)' })}</a>
                <a className="btn btn-ghost btn-sm" href={api.pdfUrl(id, 'estimate-pdf')} target="_blank" rel="noreferrer">{t({ en: 'Internal Estimate (PDF)', fil: 'Internal Estimate (PDF)' })}</a>
                <Link className="btn btn-primary btn-sm" to={`/projects/${id}/outputs`}>{t({ en: 'Continue to Bid & Proposal →', fil: 'Magpatuloy sa Bid & Proposal →' })}</Link>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Current counts */}
      {rows.length > 0 && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2>{t({ en: 'Symbols in scope', fil: 'Mga symbol na nasa scope' })}</h2>
          <table>
            <thead><tr><th>{t({ en: 'System', fil: 'System' })}</th><th>Symbol</th><th>{t(G.description)}</th><th>Device type</th><th>{t({ en: 'Current QTY', fil: 'Kasalukuyang QTY' })}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.symbol_id}>
                  <td className="muted">{r.system_name ?? '—'}</td>
                  <td><strong>{r.symbol_tag ?? '—'}</strong></td>
                  <td>{r.legend_description ?? '—'}</td>
                  <td>{r.device_type ?? <Hint text={HINTS.needs_mapping}><span style={{ color: 'var(--danger)' }}>{t({ en: 'needs mapping', fil: 'kailangan ng mapping' })}</span></Hint>}</td>
                  <td>{r.count != null ? r.count : <span className="muted">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReportCard({ report }: { report: ValidationReport }) {
  const { t } = useLang();
  const problems: [string, string[]][] = [
    [t({ en: 'Unmatched row IDs', fil: 'Hindi tugmang row IDs' }), report.unmatched],
    [t({ en: 'Non-numeric quantities', fil: 'Hindi-numero na quantities' }), report.non_numeric],
    [t({ en: 'Negative quantities', fil: 'Negatibong quantities' }), report.negatives],
    [t({ en: 'Duplicate symbols', fil: 'Dobleng symbols' }), report.duplicates],
    [t({ en: 'Missing / blank quantities', fil: 'Kulang / blankong quantities' }), report.blanks],
  ];
  return (
    <div className={`card ${report.ok ? '' : ''}`} style={{ marginTop: 16, borderColor: report.ok ? 'var(--teal)' : 'var(--danger)' }}>
      <h2>{t({ en: 'Import validation report', fil: 'Import validation report' })}</h2>
      <div className={`banner ${report.ok ? '' : 'banner-danger'}`}>
        {report.ok
          ? <><strong>{t({ en: 'Clean import.', fil: 'Malinis na import.' })}</strong> {report.accepted} {t({ en: 'of', fil: 'sa' })} {report.total_symbols} {t({ en: 'symbols counted with no issues.', fil: 'symbols ang nabilang nang walang isyu.' })}</>
          : <><strong>{t({ en: 'Imported with issues.', fil: 'Na-import pero may isyu.' })}</strong> {report.accepted} {t({ en: 'of', fil: 'sa' })} {report.total_symbols} {t({ en: 'accepted — resolve the flags below, then re-import.', fil: 'ang tinanggap — ayusin ang mga flag sa baba, tapos i-import ulit.' })}</>}
      </div>
      <p className="muted" style={{ marginTop: 0 }}>{t({ en: 'Rows received:', fil: 'Rows na natanggap:' })} {report.rows_received} · {t({ en: 'matched by hidden Row ID (never by position).', fil: 'tinugma gamit ang hidden Row ID (hindi base sa posisyon).' })}</p>
      <table>
        <tbody>
          {problems.map(([label, items]) => (
            <tr key={label}>
              <td style={{ whiteSpace: 'nowrap' }}><strong>{label}</strong></td>
              <td>{items.length === 0 ? <span className="muted">{t({ en: 'none', fil: 'wala' })}</span>
                : <span style={{ color: 'var(--danger)' }}>{items.length}: {items.slice(0, 20).join(', ')}{items.length > 20 ? '…' : ''}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  a.remove(); URL.revokeObjectURL(url);
}
